import { DRIVE_API_URL } from '../api/endpoints';
import type { PublicFileRequest, PublicTransfer } from '../types';
import { resolveStorageUrl, uploadBytes } from './api';

// Anonymous share-link endpoints. These live at the API ROOT (/s, /t, /r) —
// outside /api/drive/v1 and outside the auth gate — so they deliberately do not
// go through `driveApi`: no credentials, and a 401 means "password required",
// never "session expired" (which would bounce the visitor to platform auth).

export class PublicApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = 'PublicApiError';
  }
}

/** True when the API is telling us a password is needed or was wrong. */
export const isPasswordError = (e: unknown): boolean =>
  e instanceof PublicApiError && e.status === 401;

async function publicFetch<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(`${DRIVE_API_URL}${path}`, init);
  } catch {
    throw new PublicApiError('Could not reach SK Drive. Check your connection and try again.', 0);
  }
  if (!resp.ok) {
    const body = await resp.json().catch(() => null);
    throw new PublicApiError(
      (body as { message?: string } | null)?.message ?? 'This link is unavailable.',
      resp.status,
    );
  }
  return resp.json() as Promise<T>;
}

const pw = (password?: string) => (password ? `?password=${encodeURIComponent(password)}` : '');

// ─── Transfers: /t/:token ────────────────────────────────────────────────────
export const fetchPublicTransfer = (token: string, password?: string) =>
  publicFetch<PublicTransfer>(`/t/${encodeURIComponent(token)}${pw(password)}`);

export async function downloadTransferFile(
  token: string,
  fileId: string,
  filename: string,
  password?: string,
): Promise<void> {
  const q = new URLSearchParams({ file_id: fileId, ...(password ? { password } : {}) });
  const signed = await publicFetch<{ url: string; expires_in: number }>(
    `/t/${encodeURIComponent(token)}/download?${q.toString()}`,
  );
  const resp = await fetch(resolveStorageUrl(signed.url));
  if (!resp.ok) throw new PublicApiError('Download failed.', resp.status);
  const blob = await resp.blob();
  const obj = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(obj), 1000);
}

// ─── File requests: /r/:token ────────────────────────────────────────────────
export const fetchPublicFileRequest = (token: string) =>
  publicFetch<PublicFileRequest>(`/r/${encodeURIComponent(token)}`);

interface PublicUploadSession {
  session_id: string;
  mode: 'single' | 'multipart';
  expires_at: string;
  upload_url?: string;
  part_urls?: { partNumber: number; url: string }[];
}

// Multipart threshold and part size mirror the authenticated uploader so the
// anonymous path exercises the same Core pipeline.
const PART_SIZE = 8 * 1024 * 1024;
const MULTIPART_THRESHOLD = PART_SIZE;

/**
 * Upload one file into a file request as an anonymous visitor:
 * start session → PUT bytes → complete. Progress is reported in bytes sent.
 */
export async function uploadToFileRequest(
  token: string,
  file: File,
  onProgress?: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<{ item_id: string }> {
  const multipart = file.size > MULTIPART_THRESHOLD;
  const partCount = multipart ? Math.ceil(file.size / PART_SIZE) : 1;

  const { upload } = await publicFetch<{ upload: PublicUploadSession }>(`/r/${encodeURIComponent(token)}`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      size_bytes: file.size,
      mode: multipart ? 'multipart' : 'single',
      part_count: partCount,
      ...(file.type ? { mime_type: file.type } : {}),
    }),
    ...(signal ? { signal } : {}),
  });

  const parts: { partNumber: number; etag: string }[] = [];
  if (upload.mode === 'multipart') {
    const partUrls = upload.part_urls ?? [];
    if (partUrls.length !== partCount) throw new PublicApiError('Upload could not be prepared.', 500);
    let sent = 0;
    for (const part of partUrls) {
      const start = (part.partNumber - 1) * PART_SIZE;
      const chunk = file.slice(start, Math.min(start + PART_SIZE, file.size));
      const base = sent;
      const etag = await uploadBytes(part.url, chunk, loaded => onProgress?.(base + loaded), signal);
      // Core needs every part's ETag to assemble the object.
      if (!etag) throw new PublicApiError('Upload failed: storage did not acknowledge a part.', 502);
      parts.push({ partNumber: part.partNumber, etag });
      sent += chunk.size;
    }
  } else {
    if (!upload.upload_url) throw new PublicApiError('Upload could not be prepared.', 500);
    await uploadBytes(upload.upload_url, file, onProgress, signal);
  }

  return publicFetch<{ item_id: string; received: boolean }>(
    `/r/${encodeURIComponent(token)}/complete`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ session_id: upload.session_id, name: file.name, ...(parts.length ? { parts } : {}) }),
      ...(signal ? { signal } : {}),
    },
  );
}
